import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { addToCart, buildSingleMessage, waLink } from "../lib/cart";
import { formatIDR, splitColors, splitSizes } from "../data/products";
import { MessageCircle, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";

const ProductQuickView = ({ product, open, onOpenChange }) => {
  const { sizes, colors, hasOther } = useMemo(() => {
    if (!product) return { sizes: [], colors: [], hasOther: false };
    const sz = splitSizes(product.sizes);
    const colorsAll = splitColors(product.colors);
    const otherIdx = colorsAll.findIndex((c) =>
      c.toLowerCase().includes("other colors")
    );
    return {
      sizes: sz,
      colors: otherIdx >= 0 ? colorsAll.slice(0, otherIdx) : colorsAll,
      hasOther: otherIdx >= 0,
    };
  }, [product]);

  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);

  // Reset state whenever the dialog opens for a new product
  useEffect(() => {
    if (open && product) {
      setQty(1);
      setSize(sizes[0] || "");
      setColor(colors[0] || "");
    }
  }, [open, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!product) return null;

  const handleAdd = () => {
    addToCart(product, { size, color, qty });
    toast.success("Added to bag", {
      description: `${product.name} · ${size} · ${color}`,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="product-quickview"
        className="bg-[hsl(var(--background))] max-w-4xl p-0 overflow-y-auto max-h-[92vh] border-[hsl(var(--border))]"
      >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          data-testid="qv-close"
          aria-label="Close"
          className="absolute right-3 top-3 z-20 w-9 h-9 rounded-full bg-[hsl(var(--cream))] text-[hsl(var(--ink))] flex items-center justify-center shadow-md hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--cream))] transition-colors"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
        </button>
        <div className="grid md:grid-cols-2">
          <div className="bg-[hsl(var(--sand))] aspect-square md:aspect-auto md:min-h-[560px]">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-7 md:p-9 flex flex-col">
            <DialogHeader className="space-y-2 text-left">
              <p className="eyebrow">{product.materialDisplay}</p>
              <DialogTitle className="font-display text-2xl md:text-3xl text-[hsl(var(--ink))]">
                {product.name}
              </DialogTitle>
              <DialogDescription className="font-display text-xl text-[hsl(var(--ink))]">
                {formatIDR(product.price)}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-5">
              {sizes.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                    Size
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {sizes.map((s) => (
                      <button
                        key={s}
                        data-testid={`qv-size-${s}`}
                        onClick={() => setSize(s)}
                        className={`chip ${size === s ? "chip-active" : ""}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {colors.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                    Color
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {colors.map((c) => (
                      <button
                        key={c}
                        data-testid={`qv-color-${c}`}
                        onClick={() => setColor(c)}
                        className={`chip ${color === c ? "chip-active" : ""}`}
                      >
                        {color === c && <Check className="w-3 h-3 mr-1" />} {c}
                      </button>
                    ))}
                    {hasOther && (
                      <span className="text-xs text-[hsl(var(--muted-foreground))] italic self-center pl-1">
                        Other colors available
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                  Quantity
                </p>
                <div className="inline-flex items-center border border-[hsl(var(--border))] rounded-full">
                  <button
                    data-testid="qv-qty-minus"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                    className="w-9 h-9 text-lg"
                  >
                    −
                  </button>
                  <span className="w-10 text-center text-sm" data-testid="qv-qty">{qty}</span>
                  <button
                    data-testid="qv-qty-plus"
                    onClick={() => setQty((q) => q + 1)}
                    className="w-9 h-9 text-lg"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <button
                data-testid="qv-add-to-bag"
                onClick={handleAdd}
                className="btn-primary justify-center"
              >
                <ShoppingBag className="w-4 h-4" /> Add to Bag
              </button>
              <a
                href={waLink(buildSingleMessage(product, { size, color }))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="qv-whatsapp"
                className="btn-outline justify-center"
              >
                <MessageCircle className="w-4 h-4" /> Order via WhatsApp
              </a>
            </div>

            <p className="mt-5 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
              Availability, size, color, and final total will be confirmed by
              our team via WhatsApp.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductQuickView;
